/**
 * Конфигурация прав доступа к разделам системы
 *
 * Section ID - абстрактный ключ, который маппится на:
 * - Frontend: navigation item section prop
 * - Backend: @Section() decorator
 */

import { SectionPermission } from './types';

export const SECTIONS: Record<string, SectionPermission> = {
  // Coarse role/navigation gate only. Monitoring row authority is resolved
  // live by the dedicated DSC monitoring permission service.
  'monitoring': {
    description: 'Цели мониторинга и наблюдаемость клиентской инфраструктуры',
    allowedRoles: ['telemetry-operator', 'telemetry-admin'],
    readScope: {
      'telemetry-operator': ['own', 'subordinates', 'delegated'],
      'telemetry-admin': ['all'],
    },
    writeScope: {
      'telemetry-operator': ['own', 'subordinates', 'delegated'],
      'telemetry-admin': ['all'],
    },
    deleteScope: {},
    filterField: 'clientId',
  },

  // ===== ГЛАВНАЯ =====
  'dashboard': {
    description: 'Главная страница / дашборд',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
    },
    writeScope: {},
  },

  'employee-personal-data': {
    description: 'Персональные данные сотрудников',
    allowedRoles: ['admin', 'employee_personal_data_admin', 'accountant', 'manager', 'employee', 'controlled_documents_admin'],
    authenticatedEmployeeScope: {
      readScope: ['own'],
      writeScope: ['own'],
      deleteScope: [],
    },
    readScope: {
      admin: ['all'],
      employee_personal_data_admin: ['all'],
      accountant: ['all'],
      manager: ['own', 'subordinates'],
      employee: ['own', 'subordinates'],
      controlled_documents_admin: ['own'],
    },
    writeScope: {
      admin: ['all'],
      employee_personal_data_admin: ['all'],
      employee: ['own'],
      accountant: ['own'],
      manager: ['own'],
      controlled_documents_admin: ['own'],
    },
    deleteScope: {
      admin: ['all'],
      employee_personal_data_admin: [],
      accountant: [],
      manager: [],
      employee: [],
      controlled_documents_admin: [],
    },
    filterField: 'employeeId',
  },

  'employee-prompts': {
    description: 'Персональные foreground prompts текущего сотрудника',
    allowedRoles: ['admin', 'employee_personal_data_admin', 'accountant', 'manager', 'employee', 'controlled_documents_admin'],
    authenticatedEmployeeScope: {
      readScope: ['own'],
      writeScope: [],
      deleteScope: [],
    },
    readScope: {
      admin: ['own'],
      employee_personal_data_admin: ['own'],
      accountant: ['own'],
      manager: ['own'],
      employee: ['own'],
      controlled_documents_admin: ['own'],
    },
    writeScope: {},
    deleteScope: {},
    filterField: 'employeeId',
  },

  // ===== ДОКУМЕНТЫ =====
  'contracts': {
    description: 'Договоры',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
      employee: [],
      debt_caller: [],
    },
  },

  'amendments': {
    description: 'Дополнительные соглашения',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
      employee: [],
      debt_caller: [],
    },
  },

  'invoices': {
    description: 'Счета на оплату',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
      employee: [],
      debt_caller: [],
    },
    filterField: 'employeeId',
    capabilities: {
      // Разрешает изменять данные счёта после выплаты ЗП
      'override_salary_lock': ['admin'],
    },
  },

  'acts': {
    description: 'Акты выполненных работ',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
      employee: [],
      debt_caller: [],
    },
  },

  'payments': {
    description: 'Платежи',
    allowedRoles: ['admin', 'accountant'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: [],
      employee: [],
    },
    writeScope: {
      admin: ['all'],
      manager: [],
      employee: [],
    },
  },

  'incoming-payments': {
    description: 'Входящие платежи',
    allowedRoles: ['admin', 'accountant', 'manager', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      debt_caller: ['all'],
    },
  },

  'payments/logs': {
    description: 'Журнал событий платежей',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
      debt_caller: ['all'],
    },
    writeScope: {},
  },

  'payments/import-logs': {
    description: 'Журнал импорта платежей',
    allowedRoles: ['admin'],
    readScope: {
      admin: ['all'],
    },
    writeScope: {},
  },

  'claims': {
    description: 'Генерация претензий',
    allowedRoles: ['admin', 'manager', 'accountant'],
    readScope: {
      admin: ['all'],
      manager: ['all'],
      accountant: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
    },
  },

  'document-generation': {
    description: 'Генерация документов',
    allowedRoles: ['admin', 'manager'],
    readScope: {
      admin: ['all'],
      manager: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
    },
  },

  'print-batch': {
    description: 'Пакетная печать документов',
    allowedRoles: ['admin', 'manager'],
    readScope: {
      admin: ['all'],
      manager: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
    },
  },

  'document-scanner': {
    description: 'Сканер статусов документов',
    allowedRoles: ['admin', 'manager', 'controlled_documents_admin'],
    readScope: {
      admin: ['all'],
      manager: ['all'],
      controlled_documents_admin: ['all'],
      employee: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
      controlled_documents_admin: ['all'],
    },
  },

  'controlled-documents': {
    description: 'Реестр контролируемых документов',
    allowedRoles: [
      'admin',
      'accountant',
      'manager',
      'employee',
      'debt_caller',
      'controlled_documents_admin',
    ],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
      debt_caller: ['own'],
      controlled_documents_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
      debt_caller: ['own'],
      controlled_documents_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
    filterField: 'employeeId',
    recordAccess: {
      mode: 'employeeOrClient',
      employeeField: 'employeeId',
      clientField: 'clientId',
      employeeScopes: ['own', 'subordinates'],
    },
  },

  'controlled-documents/admin': {
    description: 'Администрирование контролируемых документов',
    allowedRoles: ['admin', 'controlled_documents_admin'],
    readScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
  },

  'controlled-documents/batch': {
    description: 'Пакетное создание контролируемых документов',
    allowedRoles: ['admin', 'controlled_documents_admin', 'accountant'],
    readScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
      accountant: ['all'],
    },
    writeScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
      accountant: ['all'],
    },
  },

  'business-processes': {
    description: 'Бизнес-процессы сотрудников',
    allowedRoles: ['admin', 'controlled_documents_admin', 'accountant', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
      accountant: ['all'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
    },
    writeScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
      accountant: ['all'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
    },
    deleteScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
    filterField: 'employeeId',
    recordAccess: {
      mode: 'employee',
      employeeField: 'employeeId',
      employeeScopes: ['own', 'subordinates'],
    },
  },

  'controlled-documents/templates': {
    description: 'Шаблоны контролируемых документов',
    allowedRoles: ['admin', 'controlled_documents_admin', 'accountant'],
    readScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
      accountant: ['all'],
    },
    writeScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
  },

  'controlled-documents/types': {
    description: 'Типы контролируемых документов',
    allowedRoles: ['admin', 'controlled_documents_admin', 'accountant'],
    readScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
      accountant: ['all'],
    },
    writeScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
      controlled_documents_admin: ['all'],
    },
  },

  'controlled-documents/scanner': {
    description: 'Сканер контролируемых документов',
    allowedRoles: [
      'admin',
      'accountant',
      'manager',
      'employee',
      'debt_caller',
      'controlled_documents_admin',
    ],
    readScope: {
      admin: ['all'],
      accountant: ['own'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
      debt_caller: ['own'],
      controlled_documents_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['own'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
      debt_caller: ['own'],
      controlled_documents_admin: ['all'],
    },
    filterField: 'employeeId',
    recordAccess: {
      mode: 'employeeOrClient',
      employeeField: 'employeeId',
      clientField: 'clientId',
      employeeScopes: ['own', 'subordinates'],
    },
  },

  'document-requests': {
    description: 'Заявки на правки документов',
    allowedRoles: [
      'admin',
      'employee',
      'document_requests_admin',
      'document_requests_signer',
    ],
    readScope: {
      admin: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
      document_requests_admin: ['all'],
      document_requests_signer: ['all'],
    },
    writeScope: {
      admin: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
      document_requests_admin: ['all'],
      document_requests_signer: ['all'],
    },
    deleteScope: {
      admin: ['all'],
    },
    filterField: 'createdById',
    recordAccess: {
      mode: 'employeeOrClient',
      employeeField: 'createdById',
      clientField: 'clientId',
      employeeScopes: ['own'],
    },
  },

  'useful-contacts': {
    description: 'Полезные контакты',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      debt_caller: ['all'],
    },
    deleteScope: {
      admin: ['all'],
    },
  },

  kbase: {
    description: 'База знаний',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      debt_caller: ['all'],
    },
    deleteScope: {
      admin: ['all'],
    },
  },

  'personal-notes': {
    description: 'Личные заметки сотрудника',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['own'],
      accountant: ['own'],
      manager: ['own'],
      employee: ['own'],
      debt_caller: ['own'],
    },
    writeScope: {
      admin: ['own'],
      accountant: ['own'],
      manager: ['own'],
      employee: ['own'],
      debt_caller: ['own'],
    },
    filterField: 'ownerId',
    recordAccess: {
      mode: 'employee',
      employeeField: 'ownerId',
      employeeScopes: ['own'],
    },
  },

  warehouse: {
    description: 'Склад',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      debt_caller: ['all'],
    },
    deleteScope: {
      admin: ['all'],
    },
  },

  'action-items': {
    description: 'Требует внимания',
    allowedRoles: [
      'admin',
      'accountant',
      'manager',
      'employee',
      'debt_caller',
      'document_requests_admin',
      'document_requests_signer',
      'service_center_repairs_admin',
      'subcontractor_works_admin',
      'fuel_accounting_admin',
      'llm_vector_stores_admin',
      'action_items_admin',
      'controlled_documents_admin',
    ],
    readScope: {
      admin: ['all'],
      accountant: ['own', 'subordinates', 'delegated'],
      manager: ['own', 'subordinates', 'delegated'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['own', 'subordinates', 'delegated'],
      document_requests_admin: ['own', 'subordinates', 'delegated'],
      document_requests_signer: ['own', 'subordinates', 'delegated'],
      service_center_repairs_admin: ['own', 'subordinates', 'delegated'],
      subcontractor_works_admin: ['own', 'subordinates', 'delegated'],
      fuel_accounting_admin: ['own', 'subordinates', 'delegated'],
      llm_vector_stores_admin: ['own', 'subordinates', 'delegated'],
      action_items_admin: ['all'],
      controlled_documents_admin: ['own', 'subordinates', 'delegated'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['own', 'subordinates', 'delegated'],
      manager: ['own', 'subordinates', 'delegated'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['own', 'subordinates', 'delegated'],
      document_requests_admin: ['own', 'subordinates', 'delegated'],
      document_requests_signer: ['own', 'subordinates', 'delegated'],
      service_center_repairs_admin: ['own', 'subordinates', 'delegated'],
      subcontractor_works_admin: ['own', 'subordinates', 'delegated'],
      fuel_accounting_admin: ['own', 'subordinates', 'delegated'],
      llm_vector_stores_admin: ['own', 'subordinates', 'delegated'],
      action_items_admin: ['all'],
      controlled_documents_admin: ['own', 'subordinates', 'delegated'],
    },
    deleteScope: {
      admin: ['all'],
    },
    filterField: 'employeeId',
    recordAccess: {
      mode: 'employeeOrClient',
      employeeField: 'employeeId',
      clientField: 'clientId',
      employeeScopes: ['own', 'subordinates', 'delegated'],
    },
  },

  'action-items/admin': {
    description: 'Администрирование требований внимания',
    allowedRoles: ['admin', 'action_items_admin'],
    readScope: {
      admin: ['all'],
      action_items_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      action_items_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
    },
  },

  'fuel-accounting': {
    description: 'Учет бензина',
    allowedRoles: [
      'admin',
      'accountant',
      'manager',
      'employee',
      'debt_caller',
      'fuel_accounting_admin',
    ],
    readScope: {
      admin: ['all'],
      accountant: ['own'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
      debt_caller: ['own'],
      fuel_accounting_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['own'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
      debt_caller: ['own'],
      fuel_accounting_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
      accountant: ['own'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
      debt_caller: ['own'],
      fuel_accounting_admin: ['all'],
    },
    filterField: 'employeeId',
    recordAccess: {
      mode: 'employee',
      employeeField: 'employeeId',
      employeeScopes: ['own', 'subordinates'],
    },
  },

  'fuel-accounting/admin': {
    description: 'Администрирование учета бензина',
    allowedRoles: ['admin', 'fuel_accounting_admin'],
    readScope: {
      admin: ['all'],
      fuel_accounting_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      fuel_accounting_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
      fuel_accounting_admin: ['all'],
    },
  },

  'fuel-accounting/templates': {
    description: 'Шаблоны путевых листов',
    allowedRoles: ['admin', 'fuel_accounting_admin'],
    readScope: {
      admin: ['all'],
      fuel_accounting_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      fuel_accounting_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
      fuel_accounting_admin: ['all'],
    },
  },

  'service-center-repairs': {
    description: 'Ремонт в СЦ',
    allowedRoles: [
      'admin',
      'accountant',
      'manager',
      'employee',
      'debt_caller',
      'service_center_repairs_admin',
    ],
    readScope: {
      admin: ['all'],
      accountant: ['own', 'subordinates', 'delegated'],
      manager: ['own', 'subordinates', 'delegated'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['own', 'subordinates', 'delegated'],
      service_center_repairs_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['own', 'subordinates', 'delegated'],
      manager: ['own', 'subordinates', 'delegated'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['own', 'subordinates', 'delegated'],
      service_center_repairs_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
    },
    filterField: 'createdById',
    recordAccess: {
      mode: 'employeeOrClient',
      employeeField: 'createdById',
      clientField: 'clientId',
      employeeScopes: ['own'],
    },
    capabilities: {
      'manage_service_center_documents': [
        'admin',
        'accountant',
        'manager',
        'service_center_repairs_admin',
      ],
      'manage_service_center_billing': ['admin', 'manager'],
      'override_salary_lock': ['admin'],
    },
  },

  'subcontractor-works': {
    description: 'Работы субподрядчиков',
    allowedRoles: [
      'admin',
      'accountant',
      'manager',
      'employee',
      'debt_caller',
      'subcontractor_works_admin',
    ],
    readScope: {
      admin: ['all'],
      accountant: ['own', 'subordinates', 'delegated'],
      manager: ['own', 'subordinates', 'delegated'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['own', 'subordinates', 'delegated'],
      subcontractor_works_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['own', 'subordinates', 'delegated'],
      manager: ['own', 'subordinates', 'delegated'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['own', 'subordinates', 'delegated'],
      subcontractor_works_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
    },
    filterField: 'createdById',
    recordAccess: {
      mode: 'employeeOrClient',
      employeeField: 'createdById',
      clientField: 'clientId',
      employeeScopes: ['own'],
    },
  },

  'visits': {
    description: 'Планирование визитов к клиентам',
    allowedRoles: ['admin', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
    },
  },

  'client-notes': {
    description: 'Заметки клиентов',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['all'],
    },
  },

  'delegations': {
    description: 'Делегирование клиентов между сотрудниками',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
    },
  },

  'document-lookup': {
    description: 'Поиск документа по номеру/штрихкоду',
    allowedRoles: ['admin', 'accountant', 'manager'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
    },
    writeScope: {},
  },

  // ===== СПРАВОЧНИКИ =====
  'clients': {
    description: 'Клиенты',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
      employee: [],
      debt_caller: [],
    },
    // Field-level permissions
    fieldPermissions: {
      // payByFact (ЗП по факту) - только admin может редактировать
      readOnly: {
        payByFact: ['accountant', 'manager', 'employee', 'debt_caller'],
      },
    },
  },

  'offices': {
    description: 'Дополнительные офисы клиентов',
    allowedRoles: ['admin', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
    },
  },

  'client-locations': {
    description: 'Точки локаций клиентов (парковки, входы)',
    allowedRoles: ['admin', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
    },
    writeScope: {
      admin: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates', 'delegated'],
    },
  },

  'employees': {
    description: 'Сотрудники',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'controlled_documents_admin'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      controlled_documents_admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
    },
  },

  'employee-locations': {
    description: 'GPS-трекинг местоположения сотрудников',
    allowedRoles: ['admin', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      manager: ['own', 'subordinates'],
      employee: ['own', 'subordinates'],
    },
    writeScope: {
      admin: ['own'],
      manager: ['own'],
      employee: ['own'],
    },
    filterField: 'employeeId',
  },

  // ===== КОНТАКТЫ =====
  'contacts': {
    description: 'Контакты клиентов (загрузка, привязка, синхронизация)',
    // TODO: вернуть manager/employee когда раздел будет готов к раскатке.
    allowedRoles: ['admin'],
    readScope: {
      admin: ['all'],
    },
    writeScope: {
      admin: ['all'],
    },
  },

  // ===== НАСТРОЙКИ =====
  'settings/requisites': {
    description: 'Реквизиты компаний',
    allowedRoles: ['admin', 'accountant', 'manager'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
    },
    writeScope: {
      admin: ['all'],
    },
  },

  'settings/signers': {
    description: 'Подписанты',
    allowedRoles: ['admin', 'accountant', 'manager'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
    },
    writeScope: { admin: ['all'] },
  },

  'settings/salary-rates': {
    description: 'Ставки заработной платы',
    allowedRoles: ['admin', 'manager'],
    readScope: { admin: ['all'], manager: ['all'] },
    writeScope: { admin: ['all'] },
  },

  // Чтение реквизитов компаний (для форм документов - НДС и т.д.)
  'requisites': {
    description: 'Реквизиты компаний (только чтение)',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
    },
    writeScope: {},
  },

  // Чтение подписантов (для форм документов)
  'signers': {
    description: 'Подписанты (только чтение)',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
    },
    writeScope: {},
  },

  // Чтение списка шаблонов (для выбора в формах документов)
  'templates': {
    description: 'Список шаблонов (только чтение)',
    allowedRoles: ['admin', 'accountant', 'manager'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
    },
    writeScope: {},
  },

  // Управление шаблонами (reload, настройки по умолчанию)
  'settings/templates': {
    description: 'Управление шаблонами',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
  },

  'settings/scheduler': {
    description: 'Планировщик задач',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
  },

  'settings/system': {
    description: 'Системные настройки',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
  },

  'settings/calendar': {
    description: 'Производственный календарь',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
      debt_caller: ['all'],
    },
    writeScope: {
      admin: ['all'],
    },
  },

  // ===== ОТЧЁТЫ =====
  'reports/salary': {
    description: 'Раскладка по заработной плате',
    allowedRoles: ['admin', 'accountant'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
    },
    writeScope: {
      admin: ['all'],
    },
  },

  'reports/my-salary': {
    description: 'Персональный просмотр зарплаты',
    allowedRoles: ['admin', 'employee'],
    readScope: {
      admin: ['all'],
      employee: ['own', 'subordinates'],
    },
    writeScope: {},
  },

  'reports/monthly': {
    description: 'Отчёт для месяцев',
    allowedRoles: ['admin', 'accountant'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
    },
    writeScope: {},
  },

  'reports/client-dynamics': {
    description: 'Динамика клиентов',
    allowedRoles: ['admin', 'accountant', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      debt_caller: ['all'],
    },
    writeScope: {},
  },

  'reports/debt': {
    description: 'Дебиторская задолженность',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
      debt_caller: ['all'],
    },
    writeScope: {},
  },

  'reports/trial-balance': {
    description: 'ОСВ (оборотно-сальдовая ведомость)',
    allowedRoles: ['admin', 'accountant'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
    },
    writeScope: {
      admin: ['all'],
    },
  },

  'reports/reconciliation': {
    description: 'Акты сверки',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['own', 'subordinates'],
      debt_caller: ['all'],
    },
    writeScope: {},
  },

  'reports/calls': {
    description: 'Журнал звонков (бэкап с мобильного приложения)',
    allowedRoles: ['admin', 'employee'],
    readScope: {
      admin: ['all'],
      employee: ['own', 'subordinates'],
    },
    writeScope: {},
    filterField: 'employeeId',
  },

  'reports/visits': {
    description: 'Посещаемость клиентов (визиты)',
    allowedRoles: ['admin', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      manager: ['own', 'subordinates'],
      employee: ['own'],
    },
    writeScope: {},
    filterField: 'employeeId',
  },

  'reports/sms': {
    description: 'Журнал SMS',
    allowedRoles: ['admin'],
    readScope: {
      admin: ['all'],
    },
    writeScope: {},
    filterField: 'employeeId',
  },

  // ===== АДМИН =====
  'permissions-visualizer': {
    description: 'Визуализатор системы доступа',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: {},
  },

  'audit': {
    description: 'Журнал аудита (история изменений)',
    allowedRoles: ['admin', 'manager'],
    readScope: {
      admin: ['all'],
      manager: ['all'],
    },
    writeScope: {},
  },

  // ===== АДМИНИСТРАТИВНЫЕ (только admin) =====
  'admin/backup': {
    description: 'Управление резервными копиями',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
  },

  // ===== ВНУТРЕННИЕ (admin + API-key для автоматизации) =====
  'internal/export': {
    description: 'Экспорт данных для 1С',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: {},
    allowApiKey: true,
  },

  'internal/exchange': {
    description: 'Обмен данными с 1С',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
    allowApiKey: true,
  },

  'internal/notifications': {
    description: 'Управление уведомлениями',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
    allowApiKey: true,
  },

  'internal/pdf-generator': {
    description: 'Генерация PDF документов',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
    allowApiKey: true,
  },

  'internal/geo': {
    description: 'Управление геокодированием и кэшем',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
    allowApiKey: true,
  },

  // ===== API КЛЮЧИ =====
  'api-keys': {
    description: 'Персональные API ключи для мобильных приложений',
    allowedRoles: ['admin', 'employee'],
    readScope: {
      admin: ['all'],
      employee: ['own', 'subordinates'],
    },
    writeScope: {
      admin: ['all'],
      employee: ['own', 'subordinates'],
    },
  },

  // ===== LLM ЧАТ =====
  'llm': {
    description: 'AI Чат (GPT)',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee'],
    readScope: {
      admin: ['all'],
      accountant: ['own'],
      manager: ['own'],
      employee: ['own'],
    },
    writeScope: {
      admin: ['all'],
      accountant: ['own'],
      manager: ['own'],
      employee: ['own'],
    },
  },

  'llm/vector-stores': {
    description: 'Хранилища знаний AI',
    allowedRoles: [
      'admin',
      'llm_vector_stores_admin',
      'accountant',
      'manager',
      'employee',
    ],
    readScope: {
      admin: ['all'],
      llm_vector_stores_admin: ['all'],
      accountant: ['all'],
      manager: ['all'],
      employee: ['all'],
    },
    writeScope: {
      admin: ['all'],
      llm_vector_stores_admin: ['all'],
    },
    deleteScope: {
      admin: ['all'],
      llm_vector_stores_admin: ['all'],
    },
  },

  // ===== КОНТРОЛЬ ДОСТУПА В ОФИС =====
  'door': {
    description: 'Контроль доступа в офис (дверь)',
    allowedRoles: ['admin', 'employee'],
    readScope: {
      admin: ['all'],
      employee: ['own'],
    },
    writeScope: {
      admin: ['all'],
      employee: ['own'],
    },
  },

  'door/admin': {
    description: 'Администрирование контроля доступа',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
  },

  'llm/admin': {
    description: 'Администрирование AI чата',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
  },

  // ===== УВЕДОМЛЕНИЯ =====
  'employee-notifications': {
    description: 'Персональные уведомления сотрудника',
    allowedRoles: ['admin', 'accountant', 'manager', 'employee', 'debt_caller'],
    readScope: {
      admin: ['own'],
      accountant: ['own'],
      manager: ['own'],
      employee: ['own'],
      debt_caller: ['own'],
    },
    writeScope: {
      admin: ['own'],
      accountant: ['own'],
      manager: ['own'],
      employee: ['own'],
      debt_caller: ['own'],
    },
  },

  'notification-admin': {
    description: 'Управление подписками на уведомления',
    allowedRoles: ['admin'],
    readScope: { admin: ['all'] },
    writeScope: { admin: ['all'] },
  },
};

/**
 * Список всех section ID
 */
export type SectionId = keyof typeof SECTIONS;

/**
 * Получить список всех section ID
 */
export function getAllSectionIds(): string[] {
  return Object.keys(SECTIONS);
}
